import { useT } from '../context/LanguageContext';
import React from 'react';
import { Link } from 'react-router-dom';
import { mockData } from '../mock';
import { useContent } from '../context/ContentContext';
import { SiteFieldEditor } from './admin/SiteFieldEditor';

const InstagramLogo = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><defs><radialGradient id="igg" cx="30%" cy="107%" r="150%"><stop offset="0" stopColor="#fdf497" /><stop offset=".1" stopColor="#fdf497" /><stop offset=".5" stopColor="#fd5949" /><stop offset=".65" stopColor="#d6249f" /><stop offset="1" stopColor="#285AEB" /></radialGradient></defs><rect x="2" y="2" width="20" height="20" rx="5.5" fill="url(#igg)" /><rect x="5" y="5" width="14" height="14" rx="4" fill="none" stroke="#fff" strokeWidth="1.8" /><circle cx="12" cy="12" r="3.4" fill="none" stroke="#fff" strokeWidth="1.8" /><circle cx="16.4" cy="7.6" r="1.1" fill="#fff" /></svg>
);
const FacebookLogo = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="#1877F2" /><path d="M15.5 12.9h-2.3V20h-3v-7.1H8.4v-2.6h1.8V8.6c0-1.9 1.1-3.1 3.2-3.1h2.2v2.6h-1.5c-.7 0-.9.3-.9.9v1.3h2.5l-.2 2.6Z" fill="#fff" /></svg>
);
const TikTokLogo = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="#010101" /><path d="M13.3 6h2.1c.2 1.6 1.2 2.7 2.8 2.9v2.1c-1 0-1.9-.3-2.7-.9v4.3a3.8 3.8 0 1 1-3.8-3.8c.2 0 .4 0 .6.1v2.2a1.7 1.7 0 1 0 1 1.5V6Z" fill="#69C9D0" transform="translate(-.5 -.5)" /><path d="M13.3 6h2.1c.2 1.6 1.2 2.7 2.8 2.9v2.1c-1 0-1.9-.3-2.7-.9v4.3a3.8 3.8 0 1 1-3.8-3.8c.2 0 .4 0 .6.1v2.2a1.7 1.7 0 1 0 1 1.5V6Z" fill="#EE1D52" transform="translate(.5 .5)" /><path d="M13.3 6h2.1c.2 1.6 1.2 2.7 2.8 2.9v2.1c-1 0-1.9-.3-2.7-.9v4.3a3.8 3.8 0 1 1-3.8-3.8c.2 0 .4 0 .6.1v2.2a1.7 1.7 0 1 0 1 1.5V6Z" fill="#fff" /></svg>
);
const MailLogo = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="3" fill="#fff" /><path d="M4 7l8 5.5L20 7" fill="none" stroke="#EA4335" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M4 7v10M20 7v10" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" /><path d="M4 17h3V9.5L4 7Z" fill="#C5221F" /><path d="M20 17h-3V9.5L20 7Z" fill="#34A853" /></svg>
);

const styles = {
  Instagram: { Logo: InstagramLogo, bg: 'bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7]', sub: '@refuelweekend' },
  Facebook: { Logo: FacebookLogo, bg: 'bg-[#1877F2]', sub: 'Refuel Øygarden' },
  TikTok: { Logo: TikTokLogo, bg: 'bg-gradient-to-r from-[#010101] via-[#1a1a1a] to-[#010101] ring-1 ring-white/15', sub: '@refuel.oygarden' },
  Mail: { Logo: MailLogo, bg: 'bg-gradient-to-r from-[#EA4335] via-[#c5221f] to-[#a3160f]', sub: 'info@refuel.rest' },
};

const SocialButtons = () => {
  const { t } = useT();
  const { content } = useContent();
  const socials = Array.isArray(content.site?.socials) ? content.site.socials : mockData.socials;
  return (
    <section id="kontakt" className="bg-black py-20 md:py-24 border-t border-white/5">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-6xl font-bold text-white tracking-tighter mb-4" style={{ fontFamily: "'Oktah Round', sans-serif" }}>
            {t('Følg oss')}
          </h2>
          <SiteFieldEditor title="Rediger sosiale lenker" fieldKeys={['socials']} testId="edit-site-socials-button" />
        </div>

        <div data-edit-fields="socials" className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {socials.map(({ name, href }) => {
            const { Logo, bg, sub } = styles[name] || styles.Mail;
            const cls = `group relative overflow-hidden rounded-3xl ${bg} p-[2px] hover:-translate-y-1 transition-transform shadow-xl ${href ? '' : 'opacity-60 cursor-not-allowed'}`;
            const inner = (
              <div className="relative rounded-[22px] bg-[#0b0611]/85 backdrop-blur-sm flex items-center gap-3 md:gap-4 px-3.5 md:px-6 py-3 md:py-4 h-full">
                <div className="w-11 h-11 md:w-14 md:h-14 rounded-2xl bg-white/95 flex items-center justify-center shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                  <Logo />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="block text-white text-base md:text-2xl font-bold tracking-tight truncate" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{name === 'Mail' ? t('E-post') : name}</span>
                  <span className="block text-white/50 text-[11px] md:text-xs truncate">{sub}</span>
                </div>
                <span className="text-white/60 group-hover:text-white text-2xl font-bold transition-colors">›</span>
              </div>
            );
            const testId = `social-link-${name.toLowerCase()}`;
            if (href?.startsWith('/')) return <Link key={name} to={href} data-testid={testId} className={cls}>{inner}</Link>;
            if (!href) return <div key={name} data-testid={testId} title="Lenke kjem snart" className={cls}>{inner}</div>;
            return <a key={name} href={href} target={href?.startsWith('mailto:') ? undefined : '_blank'} rel="noreferrer noopener" data-testid={testId} className={cls}>{inner}</a>;
          })}
        </div>
      </div>
    </section>
  );
};

export default SocialButtons;
